import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';

export interface DropdownProps {
	id: string;
	label: string;
	/** When true, the label is not rendered visually; `aria-label` is used instead. */
	labelHidden?: boolean;
	value: string | number;
	options: DropdownOption[];
	onChange: (value: string) => void;
}

export interface DropdownOption {
	value: string | number;
	label: string;
}

export const Dropdown = (props: DropdownProps) => {
	const { id, label, labelHidden, value, options, onChange } = props;
	const [open, setOpen] = useState(false);
	const [focused, setFocused] = useState(false);
	const [activeIndex, setActiveIndex] = useState(-1);
	const groupRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const listRef = useRef<HTMLUListElement>(null);
	const typeahead = useRef<{ query: string; timer: number | null }>({
		query: '',
		timer: null,
	});

	const labelId = `${id}-label`;
	const listboxId = `${id}-listbox`;
	const optionId = (index: number) => `${id}-option-${index}`;

	const selectedIndex = options.findIndex((option) => String(option.value) === String(value));
	const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

	// Close on outside click. Escape and keyboard navigation are handled on the
	// options themselves so focus can be returned to the trigger.
	useEffect(() => {
		if (!open) {
			return;
		}
		const handlePointerDown = (event: MouseEvent) => {
			if (!groupRef.current?.contains(event.target as Node)) {
				setOpen(false);
			}
		};
		document.addEventListener('mousedown', handlePointerDown);
		return () => {
			document.removeEventListener('mousedown', handlePointerDown);
		};
	}, [open]);

	const scrollActiveIntoView = (index: number) => {
		const el = listRef.current?.children[index];
		if (el instanceof HTMLElement) {
			el.scrollIntoView({ block: 'nearest' });
		}
	};

	// When the menu opens, highlight the currently selected option (or the
	// first option). Focus stays on the trigger throughout — the active option
	// is tracked via aria-activedescendant and the gray background, never by
	// moving DOM focus.
	useEffect(() => {
		if (open) {
			const index = selectedIndex >= 0 ? selectedIndex : 0;
			setActiveIndex(index);
			scrollActiveIntoView(index);
		} else {
			setActiveIndex(-1);
			typeahead.current.query = '';
		}
		// selectedIndex intentionally read only on the open transition
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const moveActiveTo = (index: number) => {
		if (options.length === 0) {
			return;
		}
		const next = Math.max(0, Math.min(index, options.length - 1));
		setActiveIndex(next);
		scrollActiveIntoView(next);
	};

	const selectByIndex = (index: number) => {
		const option = options[index];
		if (!option) {
			return;
		}
		onChange(String(option.value));
		setOpen(false);
		triggerRef.current?.focus();
	};

	const runTypeahead = (key: string) => {
		if (typeahead.current.timer !== null) {
			window.clearTimeout(typeahead.current.timer);
		}
		typeahead.current.query += key.toLowerCase();
		const query = typeahead.current.query;
		const match = options.findIndex((option) => option.label.toLowerCase().startsWith(query));
		if (match >= 0) {
			moveActiveTo(match);
		}
		typeahead.current.timer = window.setTimeout(() => {
			typeahead.current.query = '';
		}, 500);
	};

	const handleOpenKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
		switch (event.key) {
			case 'ArrowDown':
				event.preventDefault();
				moveActiveTo(activeIndex + 1);
				break;
			case 'ArrowUp':
				event.preventDefault();
				moveActiveTo(activeIndex - 1);
				break;
			case 'Home':
				event.preventDefault();
				moveActiveTo(0);
				break;
			case 'End':
				event.preventDefault();
				moveActiveTo(options.length - 1);
				break;
			case 'Enter':
			case ' ':
				// Prevent the native button click from re-toggling the menu.
				event.preventDefault();
				selectByIndex(activeIndex);
				break;
			case 'Escape':
				event.preventDefault();
				setOpen(false);
				break;
			case 'Tab':
				// Collapse the menu; focus is already on the trigger.
				setOpen(false);
				break;
			default:
				if (event.key.length === 1 && !event.metaKey && !event.ctrlKey) {
					runTypeahead(event.key);
				}
		}
	};

	const handleTriggerKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
		if (open) {
			handleOpenKeyDown(event);
			return;
		}
		// Menu closed: opening keys only; Enter/Space fall through to the
		// native button click that toggles the menu open.
		if (
			event.key === 'ArrowDown' ||
			event.key === 'ArrowUp' ||
			event.key === 'Home' ||
			event.key === 'End'
		) {
			event.preventDefault();
			setOpen(true);
		}
	};

	return (
		<div css={dropdownGroupStyles} ref={groupRef}>
			{!labelHidden && (
				<label id={labelId} css={dropdownLabelStyles} htmlFor={id}>
					{label}
				</label>
			)}
			<button
				id={id}
				ref={triggerRef}
				type="button"
				role="combobox"
				aria-haspopup="listbox"
				aria-expanded={open}
				aria-controls={open ? listboxId : undefined}
				aria-activedescendant={open && activeIndex >= 0 ? optionId(activeIndex) : undefined}
				{...(labelHidden ? { 'aria-label': label } : { 'aria-labelledby': `${labelId} ${id}` })}
				css={[triggerStyles, focused && triggerFocusStyles]}
				onClick={() => setOpen((prev) => !prev)}
				onKeyDown={handleTriggerKeyDown}
				onFocus={() => setFocused(true)}
				onBlur={() => setFocused(false)}
			>
				{selected?.label ?? ''}
			</button>
			{open && (
				<ul
					id={listboxId}
					ref={listRef}
					css={menuStyles}
					role="listbox"
					{...(labelHidden ? { 'aria-label': label } : { 'aria-labelledby': labelId })}
					onMouseLeave={() => setActiveIndex(selectedIndex)}
					onBlur={() => setActiveIndex(selectedIndex)}
				>
					{options.map((option, index) => {
						const isSelected = String(option.value) === String(value);
						const isActive = activeIndex === index;
						return (
							<li key={option.value} role="presentation" css={presentationItemStyles}>
								<button
									type="button"
									id={optionId(index)}
									role="option"
									aria-selected={isSelected}
									tabIndex={-1}
									css={[optionButtonStyles, isActive && itemHoveredStyles]}
									// Keep focus on the trigger when clicking.
									onMouseDown={(event) => event.preventDefault()}
									onMouseEnter={() => setActiveIndex(index)}
									onFocus={() => setActiveIndex(index)}
									onClick={() => selectByIndex(index)}
								>
									<span css={itemTextStyles}>{option.label}</span>
								</button>
							</li>
						);
					})}
				</ul>
			)}
		</div>
	);
};

const dropdownGroupStyles = css({
	position: 'relative',
	flex: 1,
	minWidth: 140,
	[media.below.sm]: {
		flexGrow: 0,
	},
});

const dropdownLabelStyles = css({
	display: 'block',
	fontFamily: wacToken('font.charlie.text'),
	fontSize: 12,
	color: wacToken('color.neutral.light700'),
	marginBlockEnd: token('space.100'),
});

const triggerStyles = css({
	width: '100%',
	appearance: 'none',
	textAlign: 'left',
	backgroundColor: wacToken('color.neutral.light0'),
	border: `1px solid ${wacToken('color.neutral.light600')}`,
	borderRadius: 20,
	padding: '8px 32px 8px 16px',
	fontFamily: wacToken('font.charlie.text'),
	fontSize: 16,
	color: wacToken('color.primary.black'),
	cursor: 'pointer',
	backgroundImage:
		"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%236b6e76' stroke-width='2' fill='none'/%3E%3C/svg%3E\")",
	backgroundRepeat: 'no-repeat',
	backgroundPosition: 'right 12px center',
});

const triggerFocusStyles = css({
	outline: `2px solid ${wacToken('color.primary.blue')}`,
	outlineOffset: 0,
});

const menuStyles = css({
	position: 'absolute',
	insetBlockStart: '100%',
	insetInline: 0,
	marginBlockEnd: 0,
	marginBlockStart: token('space.050'),
	zIndex: 1,
	boxSizing: 'border-box',
	listStyle: 'none',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	alignSelf: 'stretch',
	padding: token('space.100'),
	borderRadius: 4,
	border: `1px solid ${wacToken('color.neutral.light200')}`,
	background: wacToken('color.neutral.light0'),
	boxShadow: token('elevation.shadow.overlay'),
});

const presentationItemStyles = css({
	alignSelf: 'stretch',
	width: '100%',
});

const optionButtonStyles = css({
	display: 'flex',
	alignItems: 'center',
	width: '100%',
	gap: token('space.150'),
	padding: token('space.100'),
	borderRadius: 4,
	cursor: 'pointer',
	appearance: 'none',
	border: 'none',
	backgroundColor: wacToken('color.neutral.light0'),
	textAlign: 'left',
	font: 'inherit',
});

const itemHoveredStyles = css({
	backgroundColor: wacToken('color.neutral.light100'),
});

const itemTextStyles = css({
	color: wacToken('color.primary.black'),
	fontFeatureSettings: "'liga' off, 'clig' off",
	fontFamily: wacToken('font.charlie.text'),
	fontSize: 16,
	fontStyle: 'normal',
	fontWeight: 400,
	lineHeight: '24px',
});
